import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import { findLocale } from 'services/translation';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import {
  getSavedTimezone,
  convertDateTimezone,
  timezoneCode,
} from 'ui_system/utilities/timezoneUtils';
import { localeDateFormats } from 'views/plugins/tours/locales/localMomentDateFormats';

const unitNounMessages = defineMessages({
  Adult: { defaultMessage: 'Adult' },
  Adults: { defaultMessage: 'Adults' },
  Child: { defaultMessage: 'Child' },
  Children: { defaultMessage: 'Children' },
  Guest: { defaultMessage: 'Guest' },
  guests: { defaultMessage: 'guests' },
  Minor: { defaultMessage: 'Minor' },
  person: { defaultMessage: 'person' },
  Senior: { defaultMessage: 'Senior' },
  Seniors: { defaultMessage: 'Seniors' },
  Tickets: { defaultMessage: 'Tickets' },
});

class CheckoutSummaryRequestInfo extends React.Component {
  static displayName = 'CheckoutSummaryRequestInfo';

  static propTypes = {
    location: PropTypes.string,
    date: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    duration: PropTypes.string.isRequired,
    number_of_guests: PropTypes.number.isRequired,
    book_in_plugin: PropTypes.bool.isRequired,
    committed: PropTypes.bool.isRequired,
    root_unit: PropTypes.object.isRequired,
    origin_url: PropTypes.string,
    is_invoice: PropTypes.bool,
  };

  static defaultProps = {
    is_invoice: false,
    root_unit: {
      noun_singular: 'guest',
      noun_plural: 'guests',
    },
  };

  detailsClicked = (e) => {
    // onEditDetailsClicked is only passed on mobile
    // where we want to transition to another screen
    // on desktop we just let the click follow to it's
    // href which takes to the origin url (potentially
    // with the booking form active)
    if (typeof this.props.onEditDetailsClicked === 'function') {
      e.preventDefault();
      this.props.onEditDetailsClicked.call(e);
    } else {
      // We are in the integration and we wanna go back
      // If not it will follow the origin_url in the href
      if (this.props.book_in_plugin) {
        e.preventDefault();
        window.history.back();
      }
    }
  };

  requestAttribute = (name, suffix) => {
    const { online, tourTimezone, isoTime, is_booking_invoice, date, time } =
      this.props;
    let text = this.props[name];

    if (suffix) {
      text += ` ${suffix}`;
    }

    // Convert 12 hour time to 24 hour time
    if (name === 'time' && isoTime) {
      text = moment(time.trim(), ['h:mm A']).format('HH:mm');
    }

    if (name === 'date') {
      const locale = findLocale();
      moment.locale(locale);
      const dateFormat = localeDateFormats(locale);
      const dateObject = moment(date);

      if (dateObject.isValid()) {
        text = dateObject.format(dateFormat);
      } else {
        console.error('Invalid date');
      }
    }

    // Timezone conversion from local session
    const datetimeNames = ['time', 'date'];
    if (online && datetimeNames.includes(name)) {
      const timezone = getSavedTimezone();
      const dateTime = convertDateTimezone({
        originalTimezone: tourTimezone,
        timezone,
        date: date.trim(),
        dateFormat: 'MMM DD, YYYY',
        time: time.replace(/\s+/g, ''),
        timeFormat: 'h:mma',
      });
      moment.locale(findLocale());
      const timeFormat = isoTime ? 'HH:mm' : 'h:mm a';
      if (name === 'time')
        text = `${dateTime.format(timeFormat)} (${timezoneCode(timezone)})`;
      if (name === 'date') text = `${dateTime.format('MMM D, YYYY')}`;
    }

    if (this.isReadOnly()) {
      return <span className="value">{text}</span>;
    }

    let attributeHref = '#';
    if (!this.props.book_in_plugin) {
      attributeHref = this.props.origin_url;
    }

    return (
      <a
        href={attributeHref}
        onClick={this.detailsClicked}
        className="value brand-color"
        data-testid="checkout-card-details"
      >
        {text}
      </a>
    );
  };

  isReadOnly = () => {
    return (
      this.props.committed ||
      this.props.is_invoice ||
      this.props.is_booking_invoice
    );
  };

  renderDuration = () => {
    let view = null;

    if (this.shouldShowDuration()) {
      view = (
        <tr>
          <td className="label">
            <FormattedMessage id="DURATION" defaultMessage="Duration" />
          </td>
          <td className="value">{this.props.duration}</td>
        </tr>
      );
    }

    return view;
  };

  shouldShowDuration = () => {
    return this.props.duration > 0;
  };

  renderLocation = () => {
    return (
      this.props.locationLabel && (
        <tr>
          <td className="label">
            <FormattedMessage id="LOCATION" defaultMessage="Location" />
          </td>
          <td>{this.requestAttribute('locationLabel')}</td>
        </tr>
      )
    );
  };

  render() {
    const { intl } = this.props;
    const nounRaw = this.props?.root_unit?.noun_plural;
    const nounMessage = unitNounMessages[nounRaw];
    const unit = nounMessage ? intl.formatMessage(nounMessage) : nounRaw;

    return (
      <div className="CheckoutSummary-ContentBox Request-Information">
        <table>
          <tbody>
            {this.renderLocation()}
            <tr>
              <td className="label">
                <FormattedMessage id="DATE" defaultMessage="Date" />
              </td>
              <td>{this.requestAttribute('date')}</td>
            </tr>
            <tr>
              <td className="label">
                <FormattedMessage id="TIME" defaultMessage="Time" />
              </td>
              <td>{this.requestAttribute('time')}</td>
            </tr>
            {this.renderDuration()}
            <tr>
              <td className="label">
                <FormattedMessage
                  id="NUMBER_OF_UNIT"
                  defaultMessage="Number of {unit}"
                  values={{ unit }}
                />
              </td>
              <td>{this.requestAttribute('number_of_guests')}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(CheckoutSummaryRequestInfo);
